import { useQueryClient } from '@tanstack/react-query';
import { SubmitButtons } from '@unistocks/components/button/submit-buttons';
import { Form } from '@unistocks/components/form/form';
import { FormProps } from '@unistocks/components/form/model';
import { MultiStepForm, MultiStepFormProps } from '@unistocks/components/form/multi-step-form';
import { toast } from '@unistocks/components/toast';
import { t } from '@unistocks/localization';
import {
  DashboardItemSettingsDto,
  ListResultDtoOfDashboardItemDto
} from '@unistocks/proxy/axios-client';
import {
  setDashboardGetData,
  useDashboardUpdateDashboardItemSettingsMutation
} from '@unistocks/proxy/axios-client/DashboardQuery';
import { getComponentDisplayName } from '@unistocks/shared/utils/get-display-name';
import React from 'react';
import { GetProps } from 'tamagui';

type SettingsComponentProps<
  T extends DashboardItemSettingsFormProps | DashboardItemMultiStepSettingsFormProps
> = {
  itemId: string;
  settings: DashboardItemSettingsDto;
  onCancel: () => void;
} & Omit<T, 'onSubmit'>;

type BaseProps = {
  formId: string;
  onCancel: () => void;
};

type DashboardItemSettingsFormProps = BaseProps & FormProps<DashboardItemSettingsDto>;
type DashboardItemMultiStepSettingsFormProps = BaseProps &
  MultiStepFormProps<DashboardItemSettingsDto>;

export const withDashboardItemSettings = <
  T extends DashboardItemSettingsFormProps | DashboardItemMultiStepSettingsFormProps
>(
  DashboardItemComponent: React.ComponentType<T>
) => {
  const WithDashboardItemSettings = ({
    formId,
    itemId,
    settings,
    schema,
    onCancel,
    children,
    ...props
  }: SettingsComponentProps<T>) => {
    const queryClient = useQueryClient();

    const { mutateAsync: updateSettings } = useDashboardUpdateDashboardItemSettingsMutation(
      itemId,
      {
        onSuccess: res => {
          setDashboardGetData(queryClient, dashboard => {
            const data: ListResultDtoOfDashboardItemDto = { items: [] };

            if (dashboard?.items) {
              data.items = [...dashboard.items];

              const index = data.items.findIndex(i => i.id === res.id);
              if (index !== -1) {
                data.items[index] = res;
              }
            }

            return data;
          });

          // queryClient.invalidateQueries({ queryKey: dashboardGetQueryKey() });
          queryClient.invalidateQueries({ predicate: query => query.queryKey.includes(res.id) });

          toast.success(t('::Dashboard:Success'));
          onCancel();
        },
        onError: () => toast.error(t('::Dashboard:Error'))
      }
    );

    const Component = DashboardItemComponent as React.ComponentType<any>;

    return (
      <Component
        formId={formId}
        schema={schema}
        initialValues={settings}
        onSubmit={(values: DashboardItemSettingsDto) => updateSettings(values)}
        onCancel={onCancel}
        {...props}
      >
        {children}
      </Component>
    );
  };

  WithDashboardItemSettings.displayName = `withDashboardItemSettings(${getComponentDisplayName(DashboardItemComponent)})`;

  return WithDashboardItemSettings;
};

const DashboardItemSettingsForm: React.FC<DashboardItemSettingsFormProps> = ({
  onCancel,
  children,
  ...props
}) => {
  return (
    <Form
      px={0}
      flex={1}
      customTrigger={
        <SubmitButtons
          cancelButtonText={t('::Cancel')}
          submitButtonText={t('::Save')}
          onCancel={onCancel}
        />
      }
      {...props}
    >
      {children}
    </Form>
  );
};

const DashboardItemMultiStepSettingsForm: React.FC<DashboardItemMultiStepSettingsFormProps> = ({
  children,
  ...props
}) => {
  return (
    <MultiStepForm px={0} flex={1} strict={false} {...props}>
      {children}
    </MultiStepForm>
  );
};

export type DashboardItemSettingsProps = GetProps<typeof DashboardItemSettings>;
export type MultiStepDashboardItemSettingsProps = GetProps<typeof MultiStepDashboardItemSettings>;

export const DashboardItemSettings = withDashboardItemSettings(DashboardItemSettingsForm);
export const MultiStepDashboardItemSettings = withDashboardItemSettings(
  DashboardItemMultiStepSettingsForm
);
